import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBrush } from '@tabler/icons-react';
import { fetchCategoriesAndSubcategories, fetchEnhancedHomePage } from '../../redux/slices/client/clientHomeSlice';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { logout } from '../../redux/slices/authSlice';

import '../../App.css';

import homeHero from '../../assets/images/HomeHero.jpg';
import rightArrow from '../../assets/images/left-arrow2.png';
import leftArrow from '../../assets/images/left-arrow.png';
import shippingTruck from '../../assets/images/shipping-truck.png';
import truck from '../../assets/images/Vector.png';
import check from '../../assets/images/Vector(1).png';
import group from '../../assets/images/Group.png';
import tabler_reload from '../../assets/images/tabler_reload.png';
import diamond1 from '../../assets/images/diamond1.png';
import diamond2 from '../../assets/images/diamond2.png';
import diamond3 from '../../assets/images/diamond3.png';
import razorpayImg from '../../assets/images/razor.jpg';
import stripeImg from '../../assets/images/stripe.jpg';
import FirstTimeCouponModal from '../../components/common/modals/FirstTimeCouponModal';
import { thunkFetchCouponForFirstTimeCustomer } from '../../redux/slices/couponSlice';

import ExpiringCouponsBanner from '../../components/common/widgets/ExpiringCouponsBanner';
import ExpiringCouponsModal from '../../components/common/widgets/ExpiringCouponsModal';

// Shimmer Components
const CategoryShimmer = () => (
  <div className="bg-gray-200 flex flex-col rounded-none" style={{ minHeight: '240px' }}>
    <div className="flex-1 bg-gray-300 animate-pulse"></div>
    <div className="w-full bg-gray-400 h-10 animate-pulse"></div>
  </div>
);

const ProductShimmer = () => (
  <div className="relative flex-shrink-0 bg-gray-200 rounded-lg w-[220px] h-[330px] animate-pulse">
    <div className="w-full h-full bg-gray-300 rounded-lg"></div>
  </div>
);

const TestimonialShimmer = () => (
  <div className="flex flex-col items-center text-center">
    <div className="w-24 h-24 rounded-full bg-gray-300 mb-4 animate-pulse"></div>
    <div className="h-6 w-32 bg-gray-300 mb-4 animate-pulse"></div>
    <div className="h-16 w-48 bg-gray-300 mb-4 animate-pulse"></div>
    <div className="w-16 h-16 bg-gray-300 rounded-full animate-pulse"></div>
  </div>
);

const CategoryCarouselShimmer = () => (
  <div className="flex flex-col items-center snap-center min-w-[120px] mx-2">
    <div className="w-20 h-20 rounded-full bg-gray-300 mb-3 animate-pulse"></div>
    <div className="h-4 w-16 bg-gray-300 rounded animate-pulse"></div>
  </div>
);

const MostCustomizedShimmer = () => (
  <div className="group relative bg-gray-200 flex flex-col shadow-sm rounded-lg overflow-hidden animate-pulse" style={{ minHeight: '320px' }}>
    <div className="relative flex-1 bg-gray-300" style={{ minHeight: '200px' }}></div>
    <div className="p-3">
      <div className="h-4 bg-gray-300 mb-2 rounded"></div>
      <div className="h-3 bg-gray-300 mb-2 w-3/4 rounded"></div>
      <div className="h-4 bg-gray-300 mb-3 w-1/2 rounded"></div>
      <div className="h-8 bg-gray-300 rounded"></div>
    </div>
  </div>
);

export default function Home() {
  const carouselRef = useRef(null);
  const navigate = useNavigate();
  const featuredRef = useRef(null);
  const dispatch = useDispatch();

  const { 
    loading: homeLoading, 
    customer_comments, 
    filtered_products, 
    categories,
    most_customized_products,
    recent_customizations,
    customization_stats
  } = useSelector((state) => state.home);
  
  const [showFirstTimeCouponModal, setShowFirstTimeCouponModal] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const {
    firstTimeOffers,
    isFirstTimeCustomer,
    clientLoading: couponLoading
  } = useSelector((state) => state.coupon);

  // Separate loading states for other data
  const [productsLoading, setProductsLoading] = useState(true);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const handleCouponCopy = (code) => {
    navigator.clipboard.writeText(code)
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setProductsLoading(true);
        setTestimonialsLoading(true);

        await dispatch(fetchEnhancedHomePage()).unwrap();
        setTestimonialsLoading(false);
        setProductsLoading(false);
        await new Promise((resolve) => setTimeout(resolve, 100))
        setCategoriesLoading(false);

        if (user) {
          try {
            await dispatch(thunkFetchCouponForFirstTimeCustomer()).unwrap();
          } catch (err) {
            console.error("Failed to fetch first-time coupons:", err);
          }
        }

      } catch (err) {
        console.error("Failed to fetch data:", err);
        setProductsLoading(false);
        setTestimonialsLoading(false);
      }
    };

    fetchData();

    const interval = setInterval(() => {
      if (carouselRef.current) {
        const container = carouselRef.current;
        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: 140, behavior: 'smooth' });
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [dispatch, user]);

  useEffect(() => {
    if (isFirstTimeCustomer && firstTimeOffers.length > 0 && !couponLoading) {
      // Add small delay for better UX
      const timer = setTimeout(() => {
        setShowFirstTimeCouponModal(true);
      }, 1500); // Show modal 1.5 seconds after page loads

      return () => clearTimeout(timer);
    }
  }, [isFirstTimeCustomer, firstTimeOffers, couponLoading]);

  const scrollLeft = () => {
    if (featuredRef.current) {
      featuredRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (featuredRef.current) {
      featuredRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <ExpiringCouponsBanner />
      {/* HERO SECTION */}
      <div
        className="min-h-[70vh] bg-cover bg-center flex flex-col justify-center items-center text-center py-16 px-5"
        style={{ backgroundImage: `url(${homeHero})` }}
      >
        <h1 className="text-white font-bold text-4xl md:text-[40px] mb-4">
          Create. Customize. Print. Repeat
        </h1>
        <p className="text-white text-lg md:text-xl mb-6">
          Bring Your Ideas to Life – Order Custom Prints Now!
        </p>
        <Link
          to="/product-categories"
          className="bg-[#FFD100] text-black font-semibold text-sm py-2 px-6 
                 border border-gray-500 rounded hover:bg-[#FFCA00] inline-block"
        >
          Explore Styles, Start Designing
        </Link>
      </div>

      <div className="bg-[#EEE8E8] text-center py-20 px-5">
        <h2 className="text-2xl md:text-3xl font-bold mb-8">Create & Customize</h2>
        <div
          ref={carouselRef}
          className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory justify-center"
        >
          {categoriesLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <CategoryCarouselShimmer key={`shimmer-${i}`} />
            ))
          ) : (
            categories.map((cat) => (
              <div key={cat.name} className="flex flex-col items-center snap-center min-w-[120px] mx-2">
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mb-3">
                  <img
                    src={cat.image}
                    alt={""}
                    className="w-full h-full object-cover rounded-full"
                    onError={e => {
                      e.target.onerror = null
                    }}
                  />
                </div>
                <Link
                  to={`/category/${cat.id}`}
                  className="text-sm font-medium text-[#0B0C52] hover:underline transition"
                >
                  {cat.name}
                </Link>
              </div>
            ))
          )}
        </div>
      </div>

      {/* PRODUCT GRID SECTION */}
      <div className="bg-[#EEE8E8] py-10 px-4">
        <div className="max-w-screen-xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {categoriesLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <CategoryShimmer key={`category-shimmer-${i}`} />
            ))
          ) : (
            categories.map((category) => (
              <Link
                to={`/category/${category.id}`}
                key={category.id}
                className="group relative bg-white flex flex-col shadow-sm rounded-none overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ minHeight: '240px' }}
              >
                <div className="flex-1 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover rounded-none transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="w-full bg-[#0B0C52] text-white text-center py-3 rounded-none transition-all duration-300 group-hover:bg-[#0028a8]">
                  <p className="text-sm font-medium flex items-center justify-center gap-2">
                    {category.name}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </p>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* OUR MOST CUSTOMIZED SECTION - PRODUCT CARDS */}
      <div className="bg-[#EEE8E8] py-20 px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">Our Most Customized Products</h2>
        <p className="text-gray-700 mb-8">
          Discover what People Love to Personalize & Make Your Own!
        </p>

        {/* Most Customized Products Grid */}
        {productsLoading ? (
          <div className="max-w-screen-xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <MostCustomizedShimmer key={`most-customized-shimmer-${i}`} />
            ))}
          </div>
        ) : (
          <div className="max-w-screen-xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {most_customized_products.map((product) => (
              <div
                key={product.id}
                className="group relative bg-white flex flex-col shadow-sm rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ minHeight: '320px' }}
              >
                <div className="relative flex-1 overflow-hidden" style={{ minHeight: '200px' }}>
                  <img
                    src={product.front_side_image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Badge */}
                  <div className="absolute top-2 left-2 bg-[#FFD100] text-black px-2 py-1 rounded text-xs font-medium">
                    {product.badge}
                  </div>
                  {/* Customization Count */}
                  {product.customization_count > 0 && (
                    <div className="absolute top-2 right-2 bg-[#0B0C52] text-white px-2 py-1 rounded text-xs font-medium">
                      {product.customization_count}x
                    </div>
                  )}
                  {/* Popular badge for products with high customization count */}
                  {product.customization_count >= 5 && (
                    <div className="absolute bottom-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                      🔥 Popular
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 mb-1 text-sm line-clamp-2">{product.name}</h3>
                  <p className="text-gray-600 text-xs mb-2">{product.category.name}</p>
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-[#0B0C52] font-bold text-sm">${product.price}</p>
                    {product.customization_count > 0 && (
                      <span className="text-xs text-gray-500">
                        {product.customization_count} custom{product.customization_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button
                    className="w-full bg-[#0B0C52] text-white py-2 px-3 rounded hover:bg-[#002080] transition-all flex items-center justify-center gap-1 text-xs"
                    onClick={() => navigate(`/design/${product.subCategory.id}`)}
                  >
                    <IconBrush size={14} />
                    Customize Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show more button if there are many products */}
        {most_customized_products.length >= 6 && (
          <div className="mt-8">
            <button
              className="bg-[#0B0C52] text-white py-3 px-8 rounded-lg hover:bg-[#002080] transition-all font-medium"
              onClick={() => navigate('/product-categories')}
            >
              View All Customizable Products
            </button>
          </div>
        )}

        {/* Recent Customizations - Smaller Section */}
        {recent_customizations.length > 0 && (
          <div className="mt-16 max-w-4xl mx-auto">
            <h3 className="text-lg font-bold mb-4 text-[#0B0C52]">Recently Customized by Our Community</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {recent_customizations.slice(0, 4).map((customization) => (
                <div key={customization.id} className="bg-white p-3 rounded-lg shadow-sm border">
                  <img
                    src={customization.product.images.front_image}
                    alt={customization.name}
                    className="w-full h-24 object-cover rounded mb-2"
                  />
                  <h4 className="font-medium text-xs mb-1 line-clamp-1">{customization.name}</h4>
                  <p className="text-gray-600 text-xs">by {customization.creator_name}</p>
                  <span className="inline-block mt-1 bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                    New
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats - Compact Version */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#0B0C52] to-[#002080] text-white p-6 rounded-lg">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <h4 className="text-2xl font-bold">{customization_stats.total_customizations}</h4>
                <p className="text-sm opacity-90">Total Customizations</p>
              </div>
              <div>
                <h4 className="text-2xl font-bold">{customization_stats.this_week_customizations}</h4>
                <p className="text-sm opacity-90">This Week</p>
              </div>
              <div>
                <h4 className="text-2xl font-bold">{customization_stats.active_customizable_products}</h4>
                <p className="text-sm opacity-90">Products Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FEATURED PRODUCTS SECTION */}
      <div className="bg-[#EEE8E8] py-20 px-4 text-center">
        <h2 className="text-[#0B0C52] text-2xl md:text-[40px] font-bold mb-6 py-6">
          Featured Products
        </h2>

        <div className="relative max-w-screen-xl mx-auto overflow-x-visible">
          {productsLoading ? (
            <div className="no-scrollbar flex gap-8 overflow-x-auto scroll-smooth px-8 justify-center">
              {Array.from({ length: 4 }).map((_, i) => (
                <ProductShimmer key={`product-shimmer-${i}`} />
              ))}
            </div>
          ) : (
            <>
              {filtered_products.length > 4 && (
                <button
                  onClick={scrollLeft}
                  className="absolute left-[-2rem] top-1/2 -translate-y-1/2 text-[#0B0C52] text-xl z-10 hover:scale-110 transition-transform"
                >
                  <img src={leftArrow} alt="Left Arrow Icon" className="w-6 h-6" />
                </button>
              )}

              <div
                ref={featuredRef}
                className={`no-scrollbar flex gap-8 overflow-x-auto scroll-smooth px-8 ${filtered_products.length <= 4 ? 'justify-center' : ''
                  }`}
              >
                {filtered_products.map((item, i) => (
                  <div
                    key={i}
                    className="relative flex-shrink-0 bg-white shadow-md rounded-lg w-[220px] h-[330px] transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="relative w-full h-full flex items-center justify-center">
                      {item.back_side_image && (
                        <img
                          src={item.back_side_image}
                          alt={item.name + ' Back'}
                          className="absolute w-[180px] h-[270px] object-cover rounded-lg border border-gray-200 shadow-sm transform -rotate-6 left-4 top-4"
                          style={{ zIndex: 1 }}
                        />
                      )}
                      {item.front_side_image && (
                        <img
                          src={item.front_side_image}
                          alt={item.name + ' Front'}
                          className="absolute w-[180px] h-[270px] object-cover rounded-lg border border-gray-200 shadow-sm transform rotate-6"
                          style={{ zIndex: 2 }}
                        />
                      )}
                    </div>
                    <button
                      className="absolute z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#0B0C52] text-white px-4 py-2 flex items-center gap-2 text-sm rounded-full shadow-lg hover:bg-[#002080] transition-all"
                      onClick={() => navigate(`/design/${item.subCategory.id}`)}
                    >
                      <IconBrush size={16} />
                      Customize
                    </button>
                  </div>
                ))}
              </div>

              {filtered_products.length > 4 && (
                <button
                  onClick={scrollRight}
                  className="absolute right-[-2rem] top-1/2 -translate-y-1/2 text-[#0B0C52] text-xl z-10 hover:scale-110 transition-transform"
                >
                  <img src={rightArrow} alt="Right Arrow Icon" className="w-6 h-6" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* TESTIMONIALS SECTION */}
      <div className="bg-[#0B0C52] text-white py-16 px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">
          What Our Happy Customers Say
        </h2>
        <p className="text-center text-gray-200 mb-8">
          Real Reviews from Customers Who Love Their Personalized Creations
        </p>

        <div className="max-w-screen-xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonialsLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TestimonialShimmer key={`testimonial-shimmer-${i}`} />
            ))
          ) : (
            <>
              {customer_comments.map((comment, index) => (
                <div key={comment.id} className="flex flex-col items-center text-center">
                  <img
                    src={comment.profilePicture}
                    alt={`Customer ${comment.name}`}
                    className="w-24 h-24 rounded-full object-cover mb-4"
                  />
                  <h3 className="font-semibold text-lg">{comment.name}</h3>
                  <p className="text-sm my-4 max-w-[240px]">
                    "{comment.comment}"
                  </p>
                  <div className="w-16 h-16 overflow-hidden">
                    <img
                      src={index === 0 ? diamond1 : index === 1 ? diamond2 : diamond3}
                      alt="Custom Design"
                      className="w-full h-full transform "
                    />
                  </div>
                </div>
              ))}
              {Array.from({ length: 3 - customer_comments.length }).map((_, index) => (
                <div key={`empty-${index}`} className="flex flex-col items-center text-center opacity-0">
                  <div className="w-24 h-24 rounded-full bg-transparent mb-4"></div>
                  <div className="w-full h-8 bg-transparent mb-4"></div>
                  <div className="w-full h-32 bg-transparent mb-4"></div>
                  <div className="w-16 h-16"></div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* SHIPPING, RETURNS & POLICIES SECTION */}
      <div className="bg-[#EEE8E8] py-20 px-4">
        <h2 className="text-[#0B0C52] text-2xl md:text-[40px] font-bold text-center mb-2">
          Shipping, Returns &amp; Policies
        </h2>
        <p className="text-center text-gray-700 italic mb-8">
          "Not satisfied? No worries – we offer free returns!"
        </p>

        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-center gap-10">
          <div className="flex-1">
            <img src={shippingTruck} alt="Shipping Truck" className="w-full" />
          </div>
          <div className="flex-1 space-y-4 text-left">
            <div className="flex items-start gap-2">
              <img src={truck} alt="Truck" className="w-[78px] h-[62px]" />
              <span className="text-gray-800">Worldwide Shipping</span>
            </div>
            <div className="flex items-start gap-2">
              <img src={group} alt="Check" className="w-[78px] h-[62px]" />
              <span className="text-gray-800">Express &amp; Standard Delivery Available</span>
            </div>
            <div className="flex items-start gap-2">
              <img src={tabler_reload} alt="Check" className="w-[78px] h-[62px]" />
              <span className="text-gray-800">30-Day Hassle-Free Returns</span>
            </div>
            <div className="flex items-start gap-2">
              <img src={check} alt="Check" className="w-[78px] h-[62px]" />
              <span className="text-gray-800">
                <a href="/privacy-policy" className="text-[#0B0C52] hover:underline">Privacy Policy</a>
                &nbsp;and&nbsp;
                <a href="/terms-and-conditions" className="text-[#0B0C52] hover:underline">Terms &amp; Conditions</a>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secure Payment */}
      <div className="bg-[#EEE8E8] py-20 px-4">
        <h2 className="text-center text-2xl md:text-3xl font-bold mb-2">
          Secure Payment &amp; Trust Badges
        </h2>
        <p className="text-center text-gray-700 mb-8">
          We use industry-leading encryption to keep your information secure.
        </p>

        <div className="max-w-screen-lg mx-auto flex flex-col md:flex-row items-start md:items-center justify-center gap-10">
          <div className="flex flex-col items-center">
            <div className="flex gap-6">
              <img src={razorpayImg} alt="Razorpay" className="w-24 h-auto object-contain" />
              <img src={stripeImg} alt="Stripe" className="w-24 h-auto object-contain" />
            </div>
            <p className="mt-4 text-gray-600 font-medium">Payment Methods</p>
          </div>
          <div className="text-left space-y-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 11.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-800">SSL Secured Checkout</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 11.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-800">100% Money-Back Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 11.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-800">Safe &amp; Secure Payment Processing</span>
            </div>
          </div>
        </div>
      </div>

      <FirstTimeCouponModal
        isOpen={showFirstTimeCouponModal}
        onClose={() => setShowFirstTimeCouponModal(false)}
        coupons={firstTimeOffers}
        userEligible={isFirstTimeCustomer}
        onCouponCopy={handleCouponCopy}
      />

      <ExpiringCouponsBanner />
      {/* <ExpiringCouponsModal /> */}
    </div>
  );
}